/**
 * NAD Collection Filters
 * Based on GWG Collection Nav Widget pattern: Collection navigation tabs
 * Handles collection filter interactions and navigation
 */

export class NADCollectionFilters {
  private element: HTMLElement;
  private tabs: NodeListOf<HTMLElement>;
  private currentSelected: HTMLElement | null = null;
  private scrollObserver: IntersectionObserver | null = null;
  private isCollectionListPage: boolean = false;

  constructor(element: HTMLElement) {
    this.element = element;
    this.tabs = this.element.querySelectorAll(
      ".nad-collection-filters__tab"
    );
    this.currentSelected = this.element.querySelector(
      ".nad-collection-filters__tab--selected"
    );
    this.isCollectionListPage = !!document.querySelector(
      "body.template-list-collections"
    );

    this.init();
  }

  private init(): void {
    console.log('NADCollectionFilters: Initializing with', this.tabs.length, 'tabs');
    if (this.tabs.length === 0) {
      console.warn('NADCollectionFilters: No tabs found in element', this.element);
      return;
    }
    this.setupTabInteractions();
    
    // Only setup scroll detection on collection list pages
    if (this.isCollectionListPage) {
      this.setupScrollDetection();
    }
  }
  
  /**
   * Setup click interactions for tabs
   */
  private setupTabInteractions(): void {
    this.tabs.forEach((tab, index) => {
      const link = tab.querySelector(
        ".nad-collection-filters__tab-link"
      ) as HTMLAnchorElement;

      // Add click listener to the tab itself
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        console.log('NADCollectionFilters: Tab clicked', tab, index);
        
        if (this.isCollectionListPage) {
          const collectionId = (link?.dataset["collectionId"] as string) || "";
          const sectionId = "nad-collection-slider-" + collectionId;
          console.log('NADCollectionFilters: Going to section', sectionId);
          this.goToCollectionSection(sectionId);
        } else if (link) {
          // Navigate to the collection URL
          console.log('NADCollectionFilters: Navigating to', link.href);
          window.location.href = link.href;
        }
        
        this.selectTab(tab, index);
      });

      // Also add click listener to the link for direct navigation
      if (link) {
        link.addEventListener("click", (e) => {
          if (this.isCollectionListPage) {
            e.preventDefault();
            const collectionId = (link.dataset["collectionId"] as string) || "";
            const sectionId = "nad-collection-slider-" + collectionId;
            this.goToCollectionSection(sectionId);
            this.selectTab(tab, index);
          } else {
            // Let the link navigate naturally
            this.selectTab(tab, index);
          }
        });
      }
    });
  }

  /**
   * Go to a specific collection section
   */
  private goToCollectionSection(sectionId: string): void {
    const sectionElement = document.getElementById(sectionId);
    if (sectionElement) {
      // Scroll to the section with a top offset of 100px
      const yOffset = -160;
      const y =
        sectionElement.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }


  /**
   * Setup scroll detection for collection sections
   */
  private setupScrollDetection(): void {
    const collectionSections = document.querySelectorAll('.nad-collection-slider');
    
    if (collectionSections.length === 0) {
      console.warn('NADCollectionFilters: No collection sections found for scroll detection');
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.8 // 80% visible
    };

    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          console.log('NADCollectionFilters: Section visible', sectionId);
          this.updateActiveTabFromSection(sectionId);
        }
      });
    }, options);

    // Observe all collection sections
    collectionSections.forEach((section) => {
      this.scrollObserver?.observe(section);
    });

    console.log('NADCollectionFilters: Scroll detection setup for', collectionSections.length, 'sections');
  }

  /**
   * Update active tab based on visible section
   */
  private updateActiveTabFromSection(sectionId: string): void {
    // Extract collection ID from section ID (format: nad-collection-slider-{collectionId})
    const collectionId = sectionId.replace('nad-collection-slider-', '');
    
    // Find the corresponding tab
    const targetTab = Array.from(this.tabs).find(tab => {
      const link = tab.querySelector('.nad-collection-filters__tab-link') as HTMLAnchorElement;
      return link?.dataset['collectionId'] === collectionId;
    });

    if (targetTab && targetTab !== this.currentSelected) {
      console.log('NADCollectionFilters: Updating active tab for collection', collectionId);
      this.selectTab(targetTab, Array.from(this.tabs).indexOf(targetTab));
    }
  }

  /**
   * Select a specific tab
   */
  private selectTab(tab: HTMLElement, index: number): void {
    // Remove selected class from all tabs
    this.tabs.forEach((t) => {
      t.classList.remove("nad-collection-filters__tab--selected");
      t.setAttribute("aria-selected", "false");
    });

    // Add selected class to clicked tab
    tab.classList.add("nad-collection-filters__tab--selected");
    tab.setAttribute("aria-selected", "true");

    this.currentSelected = tab;
  }

  /**
   * Cleanup method to disconnect observers
   */
  public destroy(): void {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
      this.scrollObserver = null;
    }
  }

  /**
   * Static method to initialize all collection navigation widgets
   */
  public static initializeAll(): void {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        NADCollectionFilters.initializeAll();
      });
      return;
    }

    const widgets = document.querySelectorAll(".nad-collection-filters");
    console.log('NADCollectionFilters: Found', widgets.length, 'widgets');
    widgets.forEach((widget) => {
      new NADCollectionFilters(widget as HTMLElement);
    });
  }
}